/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useState, useEffect } from "react";
import GroupSpecialRate from "./GroupSpecialRate";
import axios from "axios"; 
import GroupExtrasModal from "./GroupExtrasModal"; // ⬅️ Añade esto arriba junto a otros imports
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import {
  DRAFT_GROUP_RATE_APPLY_PATH,
  buildDraftGroupRatePayload,
  canManageDraftGroupExtras,
} from "../reservations/groupDraftUiContracts";



const GroupSummary = ({ groupReservations, reloadGroupReservations }) => {
  const { baseCurrency, formatFromBase } = useCurrencyDisplay();
  const currentUser = JSON.parse(localStorage.getItem("palantir_user") || "null");
  const permissions = currentUser?.permissions || {};
  const canApplyGroupSpecialRate = Boolean(
    permissions["reservations.override_rates"] ||
      permissions["reservations.update"]
  );
  const canManageGroupExtras = canManageDraftGroupExtras(permissions);

  const [showSpecialRate, setShowSpecialRate] = useState(false);
  const [totals, setTotals] = useState(null);
  const [showGroupExtras, setShowGroupExtras] = useState(false);


  

const handleApplySpecialRate = async (data) => {
  console.log("DATA recibido en handleApplySpecialRate:", data);
  if (!canApplyGroupSpecialRate) {
    alert("You do not have permission to override reservation rates.");
    return;
  }
  try {
    const token = localStorage.getItem("palantir_token");

    await axios.post(
      `${process.env.REACT_APP_API_URL}${DRAFT_GROUP_RATE_APPLY_PATH}`,
      buildDraftGroupRatePayload(data),
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );


    alert("Group special rate applied successfully!");

    setShowSpecialRate(false);

    // ⚡️ Llama a la función de recarga tras aplicar la tarifa especial
    if (reloadGroupReservations) await reloadGroupReservations();
  } catch (err) {
    alert("Error applying group special rate: " + (err.response?.data?.message || err.message));
  }
};

useEffect(() => {
  const fetchGroupTotals = async (groupId) => {
    try {
      const token = localStorage.getItem("palantir_token");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/reservation-draft-groups/${groupId}/summary`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("[GroupSummary][Totals] ✅ Fetched:", res.data);
      setTotals(res.data);
    } catch (err) {
      console.error("[GroupSummary][fetchGroupTotals] ❌ Error:", err);
      setTotals(null);
    }
  };

  if (Array.isArray(groupReservations) && groupReservations.length > 0) {
    const groupId = groupReservations[0]?.reservation_group_id;
    if (groupId) {
      fetchGroupTotals(groupId);
    } else {
      console.warn("[GroupSummary] ⚠️ No reservation_group_id found in reservations.");
    }
  }
}, [groupReservations]);


  console.log("[GroupSummary][Render] Totals:", totals);

  return (
    <div
      className="card bg-dark text-light p-4 mt-3 mb-4"
      style={{
        width: "100%",
        maxWidth: "100%",
        fontSize: "0.85rem",
        zoom: "80%",
      }}
    >
      
        <div
          className="d-flex align-items-center mb-3"
          style={{ width: "100%" }}
        >
          {/* Título a la izquierda */}
          <h4 className="text-info fw-bold mb-0" style={{ flex: 1 }}>Group Summary</h4>

          {/* Totales centrados con datos reales del backend */}
          <div style={{ flex: 0.75, textAlign: "center" }}>
            {totals ? (
              <div style={{ lineHeight: "1.4" }}>
                {/* Base Total */}
                <div className="fw-bold text-info" style={{ fontSize: "1rem" }}>
                  Base Total: {formatFromBase(totals.base_total)}
                </div>

                {/* Extras  */}
                <div className="fw-bold text-secondary" style={{ fontSize: "0.95rem" }}>
                  Total Extras: {formatFromBase(totals.individual_extras_total)}
                </div>

                {/* Total general */}
                <div
                  className="fw-bold text-success mt-1"
                  style={{
                    fontSize: "1.1rem",
                    borderTop: "1px solid #555",
                    marginTop: "4px",
                    paddingTop: "4px",
                  }}
                >
                  Grand Total: {formatFromBase(totals.grand_total)}
                </div>
              </div>
            ) : (
              <div className="fw-bold text-muted" style={{ fontSize: "0.9rem" }}>
                Loading totals...
              </div>
            )}
          </div>




          {/* Botones a la derecha */}
          <div style={{ flex: 1.25, textAlign: "right" }}>
            {/* 🟩 Nuevo botón Manage Group Extras */}
            <button
              type="button"
              className="btn btn-outline-success fw-bold me-2"
              style={{
                fontSize: "0.95rem",
                padding: "3px 14px",
                marginTop: "0px",
              }}
              disabled={!canManageGroupExtras}
              onClick={() => {
                if (canManageGroupExtras) setShowGroupExtras(true);
              }}
              title={
                canManageGroupExtras
                  ? "Manage extras at group level"
                  : "No permission to manage reservation extras"
              }
            >
              Manage Group Extras
            </button>

            {/* Modal de Group Extras */}
            <GroupExtrasModal
              show={showGroupExtras}
              onClose={() => setShowGroupExtras(false)}
              groupId={groupReservations?.[0]?.reservation_group_id}
              businessUnitId={groupReservations?.[0]?.business_unit_id}
              onSaved={(res) => {
                console.log("[GroupSummary] ✅ Group extras saved:", res);
                if (reloadGroupReservations) reloadGroupReservations();
              }}
            />


            {/* 🟦 Botón original intacto */}
            <button
              type="button"
              className="btn btn-outline-primary fw-bold"
              style={{
                fontSize: "0.95rem",
                padding: "3px 14px",
                marginRight: "2px",
                marginTop: "0px",
              }}
              disabled={!canApplyGroupSpecialRate}
              onClick={() => {
                if (canApplyGroupSpecialRate) setShowSpecialRate(true);
              }}
              title={
                canApplyGroupSpecialRate
                  ? "Apply special group rate"
                  : "No permission to override reservation rates"
              }
            >
              Apply special rate for group or reservation
            </button>
          </div>


          <GroupSpecialRate
            show={showSpecialRate}
            onClose={() => setShowSpecialRate(false)}
            onApply={handleApplySpecialRate}
            groupReservations={groupReservations}
            baseCurrency={baseCurrency}
          />
        </div>





        {Object.entries(
          groupReservations
            .filter(r => r.check_in_date && r.check_out_date)
            .sort((a, b) => a.group_index - b.group_index)
            .reduce((acc, res) => {
              const key = `${res.check_in_date} → ${res.check_out_date}`;
              if (!acc[key]) acc[key] = [];
              acc[key].push(res);
              return acc;
            }, {})
        ).map(([dateKey, reservationsInDate]) => (
          <div key={dateKey} className="mb-4">
            <h5 className="text-warning fw-bold mb-3">{dateKey}</h5>

            {/* Subagrupación por categoría */}
            {Object.entries(
              reservationsInDate.reduce((acc, res) => {
                const cat = res.room_category_name || "No category";
                if (!acc[cat]) acc[cat] = [];
                acc[cat].push(res);
                return acc;
              }, {})
            ).map(([categoryName, categoryReservations]) => (
              <div key={categoryName} className="mb-2">
                <div className="fw-bold text-secondary text-uppercase mb-2">{categoryName}</div>

                {categoryReservations.map((res) => {
                  console.log("🟢 RES EN SUMMARY:", res);
                  return (
                    <div
                      key={res.id}
                      className="d-flex justify-content-between align-items-start border border-secondary rounded px-3 py-3 mb-3"
                      style={{ backgroundColor: "#1c1c1c", fontSize: "0.85rem", width: "100%" }}
                    >
                      {/* 🟦 COLUMNA 1: DATOS DE RESERVA (3 bloques internos) */}
                      <div style={{ flex: 1, display: "flex", gap: "1rem" }}>
                        {/* 1. Locator */}
                        <div style={{ flex: 1 }}>
                          <div className="fw-bold">{res.locator || `Room ${res.group_index + 1}`}</div>
                        </div>

                        {/* 2. Detalles: rate, ocupación, room */}
                        <div style={{ flex: 1, textAlign: "center" }}>
                          <div>Room: {res.room_number || "Not assigned"}</div>
                          <div>{res.adults} adults + {res.children} children</div>
                          <div>
                            {res.group_rate_name
                              ? <span className="fw-bold text-primary">{res.group_rate_name} <span className="badge bg-info ms-1">Group Rate</span></span>
                              : (res.rate_name || "No rate")
                            }
                          </div>
                     
                        </div>

                        {/* 3. Precio */}
                        <div style={{ flex: 1, textAlign: "right" }}>
                          <div className="fw-bold">
                            {res.total_price ? formatFromBase(res.total_price) : "-"}
                          </div>
                        </div>
                      </div>

                      {/* 🟩 COLUMNA 2: HUÉSPEDES (3 bloques internos) */}
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          gap: "1rem",
                          paddingLeft: "2rem",
                          borderLeft: "1px solid #444"
                        }}
                      >
                        {/* 1. Avatar */}
                        <div
                          style={{
                            flex: "0 0 48px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          {res.guests?.find(g => g.is_main_guest) ? (
                            <img
                              src={
                                res.guests.find(g => g.is_main_guest)?.customer?.photo_url ||
                                "/default-avatar.png"
                              }
                              alt="Main Guest"
                              style={{
                                width: "48px",
                                height: "48px",
                                borderRadius: "50%",
                                objectFit: "cover",
                                border: "2px solid #888"
                              }}
                              onError={(e) => {
                                if (e.target.src !== window.location.origin + "/default-avatar.png") {
                                  e.target.src = "/default-avatar.png";
                                }
                              }}
                            />
                          ) : (
                            <div className="text small"></div>
                          )}
                        </div>


                        {/* 2. Main Guest Name */}
                        <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                          <div className="fw-bold text-info">
                            {res.guests?.find(g => g.is_main_guest)
                              ? `${res.guests.find(g => g.is_main_guest)?.customer?.first_name || ""} ${res.guests.find(g => g.is_main_guest)?.customer?.last_name || ""}`
                              : <span className="text">Main guest pending</span>}
                          </div>
                        </div>

                        {/* 3. Accompanying */}
                        <div style={{ flex: 1 }}>
                          {res.guests?.some(g => !g.is_main_guest && !g.is_child) ? (
                            <>
                              <div className="fw-bold text-info mb-1">Accompanying</div>
                              {res.guests
                                .filter(g => !g.is_main_guest && !g.is_child)
                                .map((g, i) => (
                                  <p key={i} className="mb-0">
                                    {g.customer?.first_name} {g.customer?.last_name}
                                  </p>
                                ))}
                            </>
                          ) : null}
                        </div>


                        {/* 4. Children */}
                        <div style={{ flex: 1 }}>
                          {res.guests?.some(g => g.is_child) ? (
                            <>
                              <div className="fw-bold text-info mb-1">Children</div>
                              {res.guests
                                .filter(g => g.is_child)
                                .map((g, i) => (
                                  <p key={i} className="mb-0">
                                    {g.customer?.first_name} {g.customer?.last_name}
                                  </p>
                                ))}
                            </>
                          ) : null}
                        </div>

                      </div>

                    </div>


                  );
                })}

                
              </div>
            ))}
          </div>
        ))}
      </div>
    )
};

export default GroupSummary;
